/**
 * All admin actions that should be audit logged.
 * Key format: {target_type}:{action_verb}
 *
 * Categories based on APP_PERMISSIONS in app-permissions.ts
 */
export const AUDIT_ACTIONS = {
  // ============================================================
  // User Management (app:users:*)
  // ============================================================
  'user:role_change': 'Changed user app role',
  'user:restrict': 'Applied restriction to user',
  'user:unrestrict': 'Removed restriction from user (revoked)',
  'user:view_details': 'Viewed sensitive user details', // Optional: for compliance

  // ============================================================
  // System Announcements (app:announcements:*)
  // ============================================================
  'announcement:create': 'Created announcement',
  'announcement:update': 'Updated announcement',
  'announcement:delete': 'Deleted announcement',
  'announcement:publish': 'Published announcement (set active_from)',
  'announcement:unpublish': 'Unpublished announcement (cleared active_from)',

  // ============================================================
  // Products - Global Catalog (app:products:*)
  // ============================================================
  'product:create': 'Created product in global catalog',
  'product:update': 'Updated product in global catalog',
  'product:delete': 'Deleted product from global catalog',
  'product:merge': 'Merged duplicate products',

  // ============================================================
  // Content Moderation (app:moderation:*)
  // ============================================================
  'moderation:approve': 'Approved user contribution to catalog',
  'moderation:reject': 'Rejected user contribution',
  'moderation:edit': 'Edited user contribution before approval',

  // ============================================================
  // Subscriptions (app:subscriptions:*)
  // ============================================================
  'subscription:extend': 'Extended user subscription',
  'subscription:cancel': 'Cancelled user subscription',
  'subscription:refund': 'Issued refund to user',
  'subscription:grant_premium': 'Granted premium access to user',
  'subscription:revoke_premium': 'Revoked premium access from user',

  // ============================================================
  // Bulk Operations (app:bulk:*)
  // ============================================================
  'bulk:notify': 'Sent bulk notification to users',
  'bulk:export': 'Exported system-wide data',

  // ============================================================
  // System Health (app:health:*)
  // ============================================================
  'system:job_retry': 'Retried failed background job',
  'system:cache_clear': 'Cleared system cache',
  'system:maintenance_start': 'Started maintenance mode',
  'system:maintenance_end': 'Ended maintenance mode',

  // ============================================================
  // Support/Debugging (app:support:*)
  // ============================================================
  'support:view_household': 'Viewed household data for support',
  'support:impersonate': 'Impersonated user for debugging',
  'support:export_user_data': 'Exported user data (GDPR request)',

  // ============================================================
  // Translation (app:translations:*)
  // ============================================================
  'translation:override_create': 'Created translation override',
  'translation:override_update': 'Updated translation override',
  'translation:override_delete': 'Deleted translation override',
} as const;

export type AuditAction = keyof typeof AUDIT_ACTIONS;

/**
 * Target types for audit log entries.
 * Maps to the entity being acted upon.
 */
export const AUDIT_TARGET_TYPES = [
  'user',
  'announcement',
  'product',
  'contribution',
  'subscription',
  'household',
  'job',
  'translation',
  'system',
] as const;

export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number];

/**
 * Map actions to their target types for validation.
 */
export const ACTION_TARGET_MAP: Record<AuditAction, AuditTargetType> = {
  // User management
  'user:role_change': 'user',
  'user:restrict': 'user',
  'user:unrestrict': 'user',
  'user:view_details': 'user',

  // Announcements
  'announcement:create': 'announcement',
  'announcement:update': 'announcement',
  'announcement:delete': 'announcement',
  'announcement:publish': 'announcement',
  'announcement:unpublish': 'announcement',

  // Products
  'product:create': 'product',
  'product:update': 'product',
  'product:delete': 'product',
  'product:merge': 'product',

  // Moderation
  'moderation:approve': 'contribution',
  'moderation:reject': 'contribution',
  'moderation:edit': 'contribution',

  // Subscriptions
  'subscription:extend': 'subscription',
  'subscription:cancel': 'subscription',
  'subscription:refund': 'subscription',
  'subscription:grant_premium': 'user',
  'subscription:revoke_premium': 'user',

  // Bulk
  'bulk:notify': 'system',
  'bulk:export': 'system',

  // System
  'system:job_retry': 'job',
  'system:cache_clear': 'system',
  'system:maintenance_start': 'system',
  'system:maintenance_end': 'system',

  // Support
  'support:view_household': 'household',
  'support:impersonate': 'user',
  'support:export_user_data': 'user',

  // Translation
  'translation:override_create': 'translation',
  'translation:override_update': 'translation',
  'translation:override_delete': 'translation',
};

/**
 * Get the description for an audit action.
 */
export function getAuditActionDescription(action: AuditAction): string {
  return AUDIT_ACTIONS[action];
}

/**
 * Get the target type for an audit action.
 */
export function getAuditActionTargetType(action: AuditAction): AuditTargetType {
  return ACTION_TARGET_MAP[action];
}
